"use client";

import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { normalizeTimelineStoredBody } from "@/lib/email/safe-email-body-utils";
import { SafeEmailBody } from "@/components/crm/messages/components/SafeEmailBody";
import type { TabContentProps } from "@/components/shared/UniversalDetailPage/types/detailPage";
import type { Communications } from "@/src/gql/graphql";
import { CollapsibleSection } from "@/components/shared/CollapsibleSection";
import { CommunicationEmailProvenanceCard } from "./CommunicationEmailProvenanceCard";
import { useHydratedCommunicationBody } from "@/lib/hooks/use-hydrated-communication-body";

export function CommunicationsDetailsDisplay({ item }: TabContentProps<Communications>) {
  const communication = item;
  const occurredAt =
    typeof communication.occurred_at === "string"
      ? new Date(communication.occurred_at).toLocaleString()
      : "Unknown";
  const communicationType =
    typeof communication.type_label === "string"
      ? communication.type_label
      : typeof communication.type_id === "string"
        ? communication.type_id
        : "Unknown";
  const communicationDirection =
    typeof communication.direction === "string" ? communication.direction : "Unknown";
  const communicationStatus =
    typeof communication.status === "string" ? communication.status : "Unknown";
  const contactName =
    typeof communication.contact_name === "string" && communication.contact_name.trim()
      ? communication.contact_name
      : "No linked contact";
  const companyName =
    typeof communication.company_name === "string" && communication.company_name.trim()
      ? communication.company_name
      : "No linked company";

  const {
    body: resolvedBody,
    loading: bodyLoading,
    error: bodyError,
  } = useHydratedCommunicationBody({
    communicationId: communication.id,
    inlineBody: typeof communication.body === "string" ? communication.body : null,
    bodyS3Key:
      typeof communication.body_s3_key === "string" ? communication.body_s3_key : null,
    previewSnippet:
      typeof communication.body_preview === "string" ? communication.body_preview : null,
  });

  const displayBody = normalizeTimelineStoredBody(resolvedBody ?? "");

  return (
    <div className="space-y-4 md:space-y-6">
      <Card>
        <CardContent className="pt-6">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
            Operator Snapshot
          </h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3">
            <div className="rounded-lg border border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900 p-3">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Type</p>
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mt-1">{communicationType}</p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900 p-3">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Direction</p>
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mt-1">{communicationDirection}</p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900 p-3">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Status</p>
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mt-1">{communicationStatus}</p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900 p-3">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Occurred</p>
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mt-1">{occurredAt}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Who We Spoke With</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-600 dark:text-gray-400">Contact</span>
              <p className="text-gray-900 dark:text-gray-100 font-medium mt-1">{contactName}</p>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">Company</span>
              <p className="text-gray-900 dark:text-gray-100 font-medium mt-1">{companyName}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <CommunicationEmailProvenanceCard metadata={communication.metadata} />

      {displayBody.trim() || bodyLoading ? (
        <Card>
          <CardContent className="pt-6">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Conversation Notes
            </h3>
            {bodyLoading && !displayBody.trim() ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Loading full message…
              </p>
            ) : null}
            {bodyError ? (
              <p className="text-sm text-amber-700 dark:text-amber-400 mb-2">{bodyError}</p>
            ) : null}
            {displayBody.trim() ? (
              <ClampedEmailBody body={displayBody} />
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardContent className="pt-6">
          <CollapsibleSection title="Record Details" defaultCollapsed>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600 dark:text-gray-400">Type:</span>
                <p className="text-gray-900 dark:text-gray-100 font-medium">{communicationType}</p>
              </div>
              <div>
                <span className="text-gray-600 dark:text-gray-400">Direction:</span>
                <p className="text-gray-900 dark:text-gray-100 font-medium">{communicationDirection}</p>
              </div>
              <div>
                <span className="text-gray-600 dark:text-gray-400">Status:</span>
                <p className="text-gray-900 dark:text-gray-100 font-medium">{communicationStatus}</p>
              </div>
              <div>
                <span className="text-gray-600 dark:text-gray-400">Occurred:</span>
                <p className="text-gray-900 dark:text-gray-100 font-medium">{occurredAt}</p>
              </div>
            </div>
          </CollapsibleSection>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Clamps the long HTML message body to a fixed height with an in-place "Show
 * more" / "Show less" toggle. The body is sanitized HTML rendered in an
 * auto-sizing iframe (SafeEmailBody), so the plain-text ClampedText helper can't
 * wrap it — instead we cap the rendered height and reveal the rest on demand.
 * Presentation only: the full message is always one click away.
 */
function ClampedEmailBody({ body }: { body: string }) {
  const [expanded, setExpanded] = React.useState(false);

  return (
    <div className="min-w-0 text-sm text-gray-700 dark:text-gray-300">
      <div
        className="relative overflow-hidden"
        style={expanded ? undefined : { maxHeight: "18rem" }}
      >
        <SafeEmailBody body={body} variant="full" collapseQuotedReplies />
        {!expanded && (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-background to-transparent" />
        )}
      </div>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="mt-2 text-xs font-medium text-blue-600 hover:text-blue-700 hover:underline transition-colors"
      >
        {expanded ? "Show less" : "Show more"}
      </button>
    </div>
  );
}
